import React, { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@apollo/react-hooks'
import { Button, Drawer, Icon, List, notification, Typography } from 'antd'
import fileDownload from 'js-file-download'
import PropTypes from 'prop-types'

import {
  COMPLETED_MENU_PROBING_JOBS_FOR_MENU,
  MENU_PROBING_JOB,
} from '../../menus/graphql/queries'
import { formatDate } from '../services/formatter'

import LondonDateTime from './london-date-time'
import Spinner from './spinner'

const HistoryItem = ({ completedDate, probingStatus }) => (
  <Typography.Text>
    At <LondonDateTime date={completedDate} /> the probing job event returned
    with
    <Typography.Text strong> {probingStatus}</Typography.Text>
    {' status'}
  </Typography.Text>
)

HistoryItem.propTypes = {
  completedDate: PropTypes.string.isRequired,
  probingStatus: PropTypes.string.isRequired,
}

const MenuProbingHistoryDrawer = ({ menuId, buttonName, buttonSize }) => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false)
  const [menuProbingJobId, setMenuProbingJobId] = useState()

  const {
    data: { completedMenuProbingJobsForMenu } = {},
    loading: isLoadingCompletedMenuProbingJobs,
    refetch: refetchCompletedMenuProbingJobs,
  } = useQuery(COMPLETED_MENU_PROBING_JOBS_FOR_MENU, {
    variables: { menuId },
    fetchPolicy: 'no-cache',
  })

  const {
    data: { menuProbingJob = {} } = {},
    loading: isLoadingMenuProbingJob,
    refetch: refetchMenuProbingJob,
  } = useQuery(MENU_PROBING_JOB, {
    skip: !menuProbingJobId,
    variables: { id: menuProbingJobId },
    onCompleted() {
      if (!menuProbingJobId) return

      fileDownload(
        JSON.stringify(menuProbingJob.menuProbingResponse.data, null, 2),
        `${menuId}-${formatDate(menuProbingJob.completedDate)}.json`,
      )
    },
    onError({ message }) {
      notification.open({
        message: 'Download probing response failed.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (!menuProbingJobId) return
    refetchMenuProbingJob()
  }, [menuProbingJobId, refetchMenuProbingJob])

  const onButtonClick = useCallback(() => {
    refetchCompletedMenuProbingJobs()
    setIsDrawerVisible(true)
  }, [refetchCompletedMenuProbingJobs])

  const onClose = useCallback(() => setIsDrawerVisible(false), [])

  return (
    <>
      <Button size={buttonSize} onClick={onButtonClick}>
        {buttonName}
        <Icon type="eye" />
      </Button>
      {isLoadingCompletedMenuProbingJobs ? (
        <Spinner />
      ) : (
        <Drawer
          title="Probing response history"
          placement="right"
          onClose={onClose}
          visible={isDrawerVisible}
          width="50%"
        >
          <List
            size="small"
            itemLayout="horizontal"
            dataSource={completedMenuProbingJobsForMenu}
            renderItem={completedMenuProbingJob => (
              <List.Item>
                <HistoryItem {...completedMenuProbingJob} />
                <Button
                  size="small"
                  loading={
                    isLoadingMenuProbingJob &&
                    completedMenuProbingJob.id === menuProbingJobId
                  }
                  onClick={() =>
                    setMenuProbingJobId(completedMenuProbingJob.id)
                  }
                >
                  Download
                  <Icon type="download" />
                </Button>
              </List.Item>
            )}
          />
        </Drawer>
      )}
    </>
  )
}

MenuProbingHistoryDrawer.propTypes = {
  menuId: PropTypes.string.isRequired,
  buttonName: PropTypes.string.isRequired,
  buttonSize: PropTypes.string,
}

MenuProbingHistoryDrawer.defaultProps = {
  buttonSize: 'small',
}

export default MenuProbingHistoryDrawer
